import { inBiliApp, isNewJsBridge } from '@bilibili/js-bridge'
import { createAdapter } from './adapters'
import { getCsrfToken, getDeviceInfo, getAppUser } from './process'
import { DEF_CONST } from './enum'
import { isNode, isFunction } from './utils'

/**
 * @type {Bjax}
 * @category 通用组件
 * @link https://www.bilibili.com/blackboard/activity-4KTjohBeB.html
 * @link https://www.bilibili.com/blackboard/activity-h33g4WcwM.html
 * @link https://s1.hdslb.com/bfs/seed/jinkela/short/bjax/inject.min.js
 * @link https://s1.hdslb.com/bfs/seed/jinkela/short/bjax/umd.min.js
 * @desc
 * - 基于`axios`封装的请求库，解决了 APP 内掉登录的问题，同时提供了`accessKey`校验的封装
 *
 * @param {object} [options] - 全局配置
 * @param {string} [options.baseURL=null] - 同`axios`的`baseURL`
 * @param {number} [options.timeout=10000/350] - 请求超时，单位毫秒，客户端为`10s`，服务端为`350ms`
 * @param {function} [options.onRequest=nil] - 处理全局请求，参数是`({ data, method, url, config })`，需返回一个新的`({ data, method, url, config })`，如果缺省则用原值
 * @param {function} [options.onSuccess=nil] - 处理全局返回，类似`axios`的`interceptors.response`第一个参数，参数是`({ data, method, url, config })`，可以返回一个新的`data`，如果缺省则用原值
 * @param {function} [options.onError=nil] - 处理全局异常，参数是`Error`、`Request`，无需返回值
 * @param {function} [options.adapter=nil] - 自定义`axios`的`adapter`，如果你想让这个包在`node`端运行，你需要以指定这个参数
 * @param {string} [options.auth=auto] - 接口校验类型，支持`web`、`mobile`和`auto`
 * @param {string} [options.csrfKey=csrf] - 设置`CSRF-TOKEN`的键名
 * @param {string} [options.csrfPos=params] - 设置`csrf`的传递位置，支持`params`、`headers`
 * @param {boolean|object} [options.preload=false] - 设置`preload`的一些公共参数（*实验性功能*）
 * @param {boolean|array<string>} [options.autoInjectDevice=false] - 是否自动在参数中注入设备信息（`global.getContainerInfo`）
 * @param {boolean|function} [options.debug=false] - 开启`debug`模式，在发请求的时候会在控制台打印日志
 * @param {boolean} [options.noTsParams=false] - 默认请求会带ts参数,开启后去除ts参数
 * @example CSR 引用
 * ```javascript
 * import Bjax from '@bilibili/bjax'
 *
 * const bjax = new Bjax()
 * ```
 * @example SSR 引用
 * ```javascript
 * import Bjax from '@bilibili/bjax'
 * import httpAdapter from 'axios/lib/adapters/http'
 * import xhrAdapter from 'axios/lib/adapters/xhr'
 *
 * const isNode = typeof window === 'undefined'
 *
 * const bjax = new Bjax({
 *   adapter: isNode ? httpAdapter : xhrAdapter
 * })
 *
 * export default bjax
 * ```
 * @example preload（*实验性功能，仅 APP 外环境有效*）
 * ```html
 * // template.html
 * 1. 在你项目的模板文件里，增加预加载的代码
 * 2. 预加载的代码要放在项目代码（vender，app，manifest，common，page...）之前以确保会更早的执行
 * 3. 使用 async 属性让预加载的 js 不会阻塞其它 HTML 部分的解析（预加载的代码量很小，应该会在其它 script 之前执行）
 * 4. 因为预加载的代码放在短缓存 CDN 节点上，因此能搭配 service worker 使用效果会更好
 * <head>
 *   <script>
 *     // 你可以在这个 script 里指定一些预加载的默认配置，如：
 *     // var __preloadCacheName = '你自定义的缓存名'
 *   </script>
 *   <script src="http://s1.hdslb.com/bfs/seed/jinkela/short/bjax/inject.min.js" async></script>
 *   <- other inject script ->
 * </head>
 * <body>
 *   ...
 * </body>
 * ```
 */
const Bjax = class {
  constructor(options = {}) {
    this.options = options
    this.adapter = null
  }

  /**
   * @desc
   * - GET 方法
   * - `content-type`只支持`application/json`
   * @template T=any
   * @param {string} url=null - 请求的 url
   * @param {object} [params = {}] - 请求的参数
   * @param {object} [config = {}] - 请求的配置
   * @param {object} [config.headers = {}] - 同`axios`的`headers`
   * @param {number} [config.timeout=0] - 接口请求超时设置，单位毫秒
   * @param {function} [config.paramsSerializer=encodeURIComponent、stringify] - 作用于`GET`方法，同`axios`的`paramsSerializer`
   * @param {function} [config.transformResponse=axios.defaults.transformResponse] - 同`axios`的`transformResponse`
   * @param {string} [config.baseURL=null] - 同`axios`的`baseURL`
   * @param {string} [config.responseType=json] - 同`axios`的`responseType`，支持`json`和`text`
   * @param {string} [config.auth=auto] - 接口校验类型，支持`web`、`mobile`和`auto`
   * @param {string} [config.csrfKey=csrf] - 设置`CSRF-TOKEN`的键名，设置为空字符串则不注入`csrf`
   * @param {string} [config.csrfPos=params] - 设置`csrf`的传递位置，支持`params`、`headers`
   * @param {boolean} [config.withCredentials=true] - 设置是否携带`cookie`，只在 web 端有效
   * @param {boolean} [config.nodeRetry=true] - 设置`Node`端发请求是否重试一次，默认重试
   * @param {boolean|object} [config.preload=false] - 设置`preload`的行为（*实验性功能*）
   * @param {boolean|array<string>} [config.autoInjectDevice=false] - 是否自动在参数中注入设备信息（`global.getContainerInfo`）
   * @param {boolean} [options.noTsParams=false] - 默认请求会带ts参数,开启后去除ts参数
   * @returns {Promise<T>} 是`axios`的`response.data`
   * @example preload
   * ```javascript
   * const http = new Bjax({
   *   preload: {
   *     cacheName: 'custom-cache-name' // 默认为 window.location.pathname
   *   }
   * })
   *
   * http.get('//api.bilibili.com/x/web-interface/nav', {}, {
   *   preload: true // <-- 设为`true`则在下次页面访问的时候，会预取该接口数据
   * })
   *
   * http.get('//api.bilibili.com/x/web-interface/xxx', { id: 'foo', slug: 'bar', csrf: 'baz' }, {
   *   preload: {
   *     queryMap: {
   *       id: 'path[.<number>]', // 会从 location.pathname 里获取，参数通过 / 分割后的 index（默认为最后一个）
   *       slug: 'query[.<string>]', // 会从 location.search 里获取，默认 key 相同（如可设置为：query.spm_id）
   *       csrf: 'cookie[.<string>]', // 会从 document.cookie 里获取，默认 key 相同（如可设置为：cookie.bili_jct）
   *     }
   *   }
   * })
   * ```
   */
  get(url = '', params = {}, config = {}) {
    return this._request(DEF_CONST.method.GET, url, params, config)
  }

  /**
   * @desc
   * - POST 方法
   * - 默认的`content-type`是`application/x-www-form-urlencoded`
   * @template T=any
   * @param {string} url=null - 请求的 url
   * @param {object} [data = {}] - 请求的参数
   * @param {object} [config = {}] - 请求的配置
   * @param {object} [config.headers = {}] - 同`axios`的`headers`
   * @param {number} [config.timeout=0] - 接口请求超时设置，单位毫秒
   * @param {function} [config.dataSerializer=encodeURIComponent、stringify] - 作用于`POST`方法，类似于`axios`的`paramsSerializer`
   * @param {function} [config.transformResponse=axios.defaults.transformResponse] - 同`axios`的`transformResponse`
   * @param {string} [config.baseURL=null] - 同`axios`的`baseURL`
   * @param {string} [config.responseType=json] - 同`axios`的`responseType`，支持`json`和`text`
   * @param {string} [config.auth=auto] - 接口校验类型，支持`web`、`mobile`和`auto`
   * @param {string} [config.csrfKey=csrf] - 设置`CSRF-TOKEN`的键名，设置为空字符串则不注入`csrf`
   * @param {string} [config.csrfPos=params] - 设置`csrf`的传递位置，支持`params`、`headers`
   * @param {boolean} [config.withCredentials=true] - 设置是否携带`cookie`，只在 web 端有效
   * @param {boolean} [config.nodeRetry=true] - 设置`Node`端发请求是否重试一次，默认重试
   * @param {boolean|array<string>} [config.autoInjectDevice=false] - 是否自动在参数中注入设备信息（`global.getContainerInfo`）
   * @param {boolean} [options.noTsParams=false] - 默认请求会带ts参数,开启后去除ts参数
   * @returns {Promise<T>} 是`axios`的`response.data`
   */
  post(url = '', data = {}, config = {}) {
    return this._request(DEF_CONST.method.POST, url, data, config)
  }

  /**
   * @desc
   * 上传图片，不支持接收`base64`的接口（接口应该支持的是二进制文件）
   * @template T=any
   * @param {string} url - 接口地址
   * @param {FormData} form - formData
   * @param {object} [config = {}] - 请求的配置
   * @param {object} [config.headers = {}] - 同`axios`的`headers`
   * @param {string} [config.csrfKey=csrf] - 设置`CSRF-TOKEN`的键名，设置为空字符串则不注入`csrf`
   * @param {string} [config.csrfPos=params] - 设置`csrf`的传递位置，支持`params`、`headers`
   * @returns {Promise<T>} 是`axios`的`response.data`
   */
  upload(url, form, config = {}) {
    return this._request(DEF_CONST.method.UPLOAD, url, form, config)
  }

  /**
   * @desc
   * - 获取用户信息，如果是客户端内则会通过`bridge`获取本地信息而不发请求
   * - 当用户未登录的时候，`then`的值为`null`
   * - 当`bridge`调用异常/网络异常的时候，会走到`catch`
   * @param {string} [api=//api.bilibili.com/x/web-interface/nav] - 可以手动设置调用接口
   * @returns {Promise<{ mid, avatar, nickname }|null>} 分别是用户的`mid`、头像、昵称
   */
  user(api = DEF_CONST.api.nav) {
    return new Promise((resolve, reject) => {
      if (inBiliApp && isNewJsBridge()) {
        getAppUser().then(resolve).catch(reject)
      } else {
        this.get(api)
          .then((res) => {
            if (res.code) {
              resolve(null)
              return
            }
            const data = res.isLogin === undefined ? res.data : res
            if (!data.isLogin) {
              resolve(null)
              return
            }
            resolve({
              mid: data.mid,
              avatar: data.face,
              nickname: data.uname
            })
          })
          .catch(reject)
      }
    })
  }

  /**
   * @desc
   * - APP外从`document.cookie`获取，APP内走`net.getCsrf`
   * - 获取当前用户的`CSRF-TOKEN`，一般情况下不需要手动调用，包内部会封装好
   * - 无需捕获`catch`
   * @param {string} [ctx=document.cookie 或 net.getCsrf] - 手动设置`cookie`的源
   * @returns {Promise<string>} 返回当前用户的`CSRF-TOKEN`
   */
  csrf(ctx) {
    return getCsrfToken(ctx)
  }

  /**
   * @desc
   * - 获取设备信息，如果是 APP 外则只返回`buvid3`
   * - 无需捕获`catch`
   * @returns {Promise<any>} 返回值同`bridge`中的`global.getContainerInfo`
   */
  device(config = {}) {
    config.autoInjectDevice = config.autoInjectDevice ?? true
    return getDeviceInfo(this.options, config)
  }

  /**
   * @param {string} method
   * @param {string} url
   * @param {object} [data]
   * @param {object} [config]
   * @returns {Promise<object>}
   * @private
   */
  async _request(method, url = '', data = {}, config = {}) {
    if (!this.adapter) {
      this.adapter = await createAdapter(this.options)
    }

    let promiseResp

    if (method === DEF_CONST.method.UPLOAD) {
      promiseResp = this.adapter.upload(url, data, config)
    } else if (this.adapter.bridge) {
      promiseResp = this.adapter.bridge(method, url, data, config)
    }

    if (!promiseResp) {
      promiseResp = this.adapter.axios(method, url, data, config)
    }

    if (this.options.onError && isFunction(this.options.onError)) {
      return new Promise((resolve, reject) => {
        promiseResp.then(resolve).catch((err) => {
          this.options.onError(err, { url, data, config, method })

          reject(err)
        })
      })
    }

    return promiseResp
  }
}

if (!isNode) {
  window.Bjax = Bjax
}

export default Bjax
